/**
 * SubscriptionPanel Component
 * Shows subscription status, pricing plans, and upgrade prompts
 * Now with TON blockchain payment support!
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TimerIcon from '@mui/icons-material/Timer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TelegramIcon from '@mui/icons-material/Telegram';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { subscriptionService, SubscriptionStatus, PricingPlan, PromoValidation, PlanId } from '@services/subscriptionService';
import { telegram } from '@services/telegram';
import { TonPaymentPanel } from './TonPaymentPanel';

// Charity percentages
const CHARITY_PERCENT: Record<string, number> = {
  individual_monthly: 10,
  individual_yearly: 20,
  business_starter: 20,
  business_team: 20,
  business_fleet: 20,
  business_enterprise: 20,
  // Legacy support
  monthly: 10,
  yearly: 20,
  business_plus: 30,
};

interface SubscriptionPanelProps {
  open: boolean;
  onClose: () => void;
  onSubscribed?: () => void;
}

export function SubscriptionPanel({ open, onClose, onSubscribed }: SubscriptionPanelProps) {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('individual_yearly');
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState<PromoValidation | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'ton' | 'stars'>('card');
  const [tonPaymentSuccess, setTonPaymentSuccess] = useState(false);
  const [tonPaymentError, setTonPaymentError] = useState<string | null>(null);
  const [starsProcessing, setStarsProcessing] = useState(false);

  const plans = subscriptionService.getPricingPlans();
  const benefits = subscriptionService.getSubscriptionBenefits();
  const user = telegram.getUser();

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open]);

  const loadStatus = async () => {
    setLoading(true);
    const subscriptionStatus = await subscriptionService.getStatus(true);
    setStatus(subscriptionStatus);
    setLoading(false);
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    const validation = await subscriptionService.validatePromo(promoCode.trim());
    setPromoValidation(validation);
    setPromoLoading(false);
    
    if (validation.valid) {
      telegram.hapticFeedback('success');
    } else {
      telegram.hapticFeedback('error');
    }
  };

  const handleSubscribe = async (planId: PlanId) => {
    telegram.hapticFeedback('medium');
    
    // Apply promo if valid
    if (promoValidation?.valid && promoCode.trim()) {
      await subscriptionService.applyPromo(promoCode.trim());
    }
    
    // Initiate checkout via Telegram bot
    await subscriptionService.initiateCheckout(planId);
    
    onClose();
    if (onSubscribed) {
      onSubscribed();
    }
  };

  // Already subscribed view
  if (!loading && status?.isSubscribed) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(34, 197, 94, 0.2)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#22c55e' }} />
            Active Subscription
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>
              You're All Set! 🎉
            </Typography>
            <Typography sx={{ color: '#94a3b8', mb: 3 }}>
              {status.isTrialing 
                ? `Trial ends in ${status.trialDaysRemaining} days`
                : `Your ${status.type} subscription is active`
              }
            </Typography>
            
            <Card sx={{ 
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              mb: 3,
            }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
                  Status: {status.status?.toUpperCase()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {status.expiresAt && `Renews: ${new Date(status.expiresAt).toLocaleDateString()}`}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)', justifyContent: 'center' }}>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              telegram.showPopup({
                title: 'Cancel Subscription',
                message: 'To cancel your subscription, please message @ZiaWellNavBot with /cancel.\n\nYour access will continue until the end of your current billing period.',
                buttons: [
                  { id: 'bot', type: 'default', text: 'Open Bot' },
                  { id: 'close', type: 'cancel', text: 'Close' }
                ]
              }, (buttonId: string) => {
                if (buttonId === 'bot') {
                  telegram.openTelegramLink('https://t.me/ZiaWellNavBot?start=cancel');
                }
              });
            }}
            sx={{ 
              color: '#ef4444',
              fontSize: '0.75rem',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              }
            }}
          >
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0f172a',
          border: '1px solid rgba(96, 165, 250, 0.2)',
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(96, 165, 250, 0.2)',
        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="h6">Upgrade to Premium</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CyberSpinner size={50} />
          </Box>
        ) : (
          <>
            {/* Trial Banner */}
            {status?.canTrial && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <TimerIcon sx={{ color: '#22c55e' }} />
                <Typography sx={{ color: '#22c55e', fontWeight: 600 }}>
                  🎁 Start with 7 days FREE - No charge today!
                </Typography>
              </Box>
            )}

            {/* Pricing Plans */}
            <Box sx={{ p: 3 }}>
              {/* Charity Banner - Shows for ALL payment methods */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.15))',
                border: '1px solid rgba(34, 197, 94, 0.4)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <VolunteerActivismIcon sx={{ color: '#22c55e', fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#22c55e', fontWeight: 700 }}>
                      💚 ZiaGivesBack - Feeding Hungry Families
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#86efac' }}>
                      {selectedPlan === 'individual_yearly' || selectedPlan === 'yearly' ? '20%' : '10%'} of your subscription goes to Packs for Hunger
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Payment Method Tabs - 3 options now */}
              <Box sx={{ mb: 3 }}>
                <Tabs
                  value={paymentMethod}
                  onChange={(_, value) => {
                    setPaymentMethod(value);
                    setTonPaymentError(null);
                  }}
                  variant="fullWidth"
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: paymentMethod === 'ton' ? '#0098ea' : 
                                       paymentMethod === 'stars' ? '#f59e0b' : '#3b82f6'
                    }
                  }}
                >
                  <Tab 
                    value="card" 
                    icon={<CreditCardIcon sx={{ fontSize: 20 }} />} 
                    iconPosition="start"
                    label="Card" 
                    sx={{ 
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                      minHeight: 48,
                      '&.Mui-selected': { color: '#3b82f6' }
                    }}
                  />
                  <Tab 
                    value="stars" 
                    icon={<TelegramIcon sx={{ fontSize: 20 }} />} 
                    iconPosition="start"
                    label="Telegram ⭐" 
                    sx={{ 
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                      minHeight: 48,
                      '&.Mui-selected': { color: '#f59e0b' }
                    }}
                  />
                  <Tab 
                    value="ton" 
                    icon={<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />} 
                    iconPosition="start"
                    label="TON Crypto" 
                    sx={{ 
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                      minHeight: 48,
                      '&.Mui-selected': { color: '#0098ea' }
                    }}
                  />
                </Tabs>
              </Box>

              {/* Payment Success */}
              {tonPaymentSuccess && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 3 }}
                  onClose={() => {
                    setTonPaymentSuccess(false);
                    onClose();
                    if (onSubscribed) onSubscribed();
                  }}
                >
                  🎉 Payment successful! Your subscription is now active. Thank you for supporting hungry families!
                </Alert>
              )}

              {/* Payment Error */}
              {tonPaymentError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 3 }}
                  onClose={() => setTonPaymentError(null)}
                >
                  {tonPaymentError}
                </Alert>
              )}

              {paymentMethod === 'ton' ? (
                /* TON Crypto Payment Panel */
                <Box>
                  {/* Plan Selection for TON */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
                    {plans.map((plan) => (
                      <Card
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        sx={{
                          flex: 1,
                          maxWidth: 200,
                          cursor: 'pointer',
                          backgroundColor: selectedPlan === plan.id 
                            ? 'rgba(0, 152, 234, 0.1)' 
                            : 'rgba(30, 41, 59, 0.8)',
                          border: selectedPlan === plan.id 
                            ? '2px solid #0098ea' 
                            : '1px solid rgba(96, 165, 250, 0.2)',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          overflow: 'visible'
                        }}
                      >
                        {plan.popular && (
                          <Chip
                            label="BEST VALUE"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -10,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#22c55e',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.6rem'
                            }}
                          />
                        )}
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                            {plan.name}
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#0098ea', fontWeight: 700 }}>
                            ${plan.price}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            /{plan.interval}
                          </Typography>
                          <Chip
                            label={`${CHARITY_PERCENT[plan.id]}% to Charity`}
                            size="small"
                            sx={{
                              mt: 1,
                              backgroundColor: 'rgba(34, 197, 94, 0.2)',
                              color: '#22c55e',
                              fontSize: '0.6rem'
                            }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                  
                  <TonPaymentPanel
                    plan={selectedPlan}
                    userId={user?.id?.toString() || ''}
                    onPaymentSuccess={() => {
                      setTonPaymentSuccess(true);
                      subscriptionService.clearCache();
                    }}
                    onPaymentError={(error) => {
                      setTonPaymentError(error);
                    }}
                  />
                </Box>
              ) : paymentMethod === 'stars' ? (
                /* Telegram Stars Payment */
                <Box>
                  {/* Plan Selection for Stars */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
                    {plans.map((plan) => (
                      <Card
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        sx={{
                          flex: 1,
                          maxWidth: 200,
                          cursor: 'pointer',
                          backgroundColor: selectedPlan === plan.id 
                            ? 'rgba(245, 158, 11, 0.1)' 
                            : 'rgba(30, 41, 59, 0.8)',
                          border: selectedPlan === plan.id 
                            ? '2px solid #f59e0b' 
                            : '1px solid rgba(96, 165, 250, 0.2)',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          overflow: 'visible'
                        }}
                      >
                        {plan.popular && (
                          <Chip
                            label="BEST VALUE"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -10,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#22c55e',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.6rem'
                            }}
                          />
                        )}
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                            {plan.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                              {plan.id.includes('monthly') ? '500' : '4,500'}
                            </Typography>
                            <Typography sx={{ color: '#f59e0b', fontSize: '1rem' }}>⭐</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            ~${plan.price} USD
                          </Typography>
                          <Chip
                            label={`${CHARITY_PERCENT[plan.id]}% to Charity`}
                            size="small"
                            sx={{
                              mt: 1,
                              backgroundColor: 'rgba(34, 197, 94, 0.2)',
                              color: '#22c55e',
                              fontSize: '0.6rem'
                            }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                  
                  {/* Stars Payment Info & Button */}
                  <Box sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(234, 179, 8, 0.1))',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" sx={{ color: '#fbbf24', mb: 2 }}>
                      Pay with Telegram Stars - instant, secure, in-app payment
                    </Typography>
                    
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={starsProcessing}
                      onClick={async () => {
                        setStarsProcessing(true);
                        telegram.hapticFeedback('medium');
                        
                        try {
                          // Create invoice via backend
                          const starsAmount = selectedPlan.includes('monthly') ? 500 : 4500;
                          const response = await fetch('/api/payments/create-invoice', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'X-Telegram-User-Id': telegram.getUserId() || '',
                            },
                            body: JSON.stringify({
                              title: `ZiaWellNav ${selectedPlan.includes('monthly') ? 'Monthly' : 'Yearly'} Premium`,
                              description: `Premium subscription with ${CHARITY_PERCENT[selectedPlan]}% to charity (ZiaGivesBack)`,
                              price: starsAmount,
                              currency: 'XTR', // Telegram Stars
                              payload: JSON.stringify({
                                type: 'subscription',
                                plan: selectedPlan,
                                charityPercent: CHARITY_PERCENT[selectedPlan],
                              })
                            })
                          });
                          
                          const data = await response.json();
                          
                          if (data.invoiceLink) {
                            // Open the invoice in Telegram
                            const tg = (window as any).Telegram?.WebApp;
                            if (tg?.openInvoice) {
                              tg.openInvoice(data.invoiceLink, (status: string) => {
                                if (status === 'paid') {
                                  setTonPaymentSuccess(true);
                                  subscriptionService.clearCache();
                                  telegram.hapticFeedback('success');
                                } else if (status === 'failed') {
                                  setTonPaymentError('Payment was not completed');
                                  telegram.hapticFeedback('error');
                                }
                                setStarsProcessing(false);
                              });
                            } else {
                              // Fallback - open link
                              window.open(data.invoiceLink, '_blank');
                              setStarsProcessing(false);
                            }
                          } else {
                            throw new Error(data.error || 'Failed to create invoice');
                          }
                        } catch (error: any) {
                          console.error('[Stars] Payment error:', error);
                          setTonPaymentError(error.message || 'Payment failed');
                          telegram.hapticFeedback('error');
                          setStarsProcessing(false);
                        }
                      }}
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        fontWeight: 700,
                        fontSize: '1rem',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        }
                      }}
                    >
                      {starsProcessing ? (
                        <CyberSpinner size={24} />
                      ) : (
                        <>⭐ Pay {selectedPlan.includes('monthly') ? '500' : '4,500'} Stars</>
                      )}
                    </Button>
                    
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 2 }}>
                      Telegram Stars can be purchased in the Telegram app
                    </Typography>
                  </Box>
                </Box>
              ) : (
                /* Card Payment (Stripe via Telegram) */
                <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    selected={selectedPlan === plan.id}
                    promoDiscount={promoValidation?.valid ? promoValidation : null}
                    onSelect={() => setSelectedPlan(plan.id)}
                    onSubscribe={() => handleSubscribe(plan.id)}
                    charityPercent={CHARITY_PERCENT[plan.id]}
                  />
                ))}
              </Box>

              {/* Promo Code Section */}
              <Box sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(96, 165, 250, 0.2)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalOfferIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Have a promo code?
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoValidation(null);
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(96, 165, 250, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(96, 165, 250, 0.5)' }
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleValidatePromo}
                    disabled={!promoCode.trim() || promoLoading}
                    sx={{
                      borderColor: '#f59e0b',
                      color: '#f59e0b',
                      '&:hover': { 
                        borderColor: '#f59e0b',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)'
                      }
                    }}
                  >
                    {promoLoading ? <CyberSpinner size={20} /> : 'Apply'}
                  </Button>
                </Box>
                {promoValidation && (
                  <Alert 
                    severity={promoValidation.valid ? 'success' : 'error'}
                    sx={{ mt: 1, backgroundColor: 'transparent' }}
                  >
                    {promoValidation.valid 
                      ? `${promoValidation.discountDisplay} applied!`
                      : promoValidation.error
                    }
                  </Alert>
                )}
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(96, 165, 250, 0.2)' }} />

              {/* Benefits */}
              <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 2, textTransform: 'uppercase' }}>
                What's Included
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                {benefits.map((benefit, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>{benefit.icon}</Typography>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(96, 165, 250, 0.2)' }}>
        <Typography variant="caption" sx={{ color: '#64748b', flex: 1 }}>
          🔒 Secure payment via {paymentMethod === 'ton' ? 'TON Blockchain' : 'Stripe'} · Cancel anytime
        </Typography>
        <Button onClick={onClose} sx={{ color: '#94a3b8' }}>
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Plan Card Component
function PlanCard({ 
  plan, 
  selected, 
  promoDiscount,
  onSelect, 
  onSubscribe,
  charityPercent
}: { 
  plan: PricingPlan;
  selected: boolean;
  promoDiscount: PromoValidation | null;
  onSelect: () => void;
  onSubscribe: () => void;
  charityPercent?: number;
}) {
  // Calculate discounted price if promo applies
  let finalPrice = plan.price;
  if (promoDiscount?.valid) {
    if (promoDiscount.appliesTo === 'all' || promoDiscount.appliesTo === plan.id) {
      if (promoDiscount.discountType === 'percent') {
        finalPrice = plan.price * (1 - (promoDiscount.discountValue! / 100));
      } else {
        finalPrice = Math.max(0, plan.price - (promoDiscount.discountValue! / 100));
      }
    }
  }
  const hasDiscount = finalPrice !== plan.price;

  return (
    <Card
      onClick={onSelect}
      sx={{
        width: 280,
        cursor: 'pointer',
        backgroundColor: selected ? 'rgba(96, 165, 250, 0.1)' : 'rgba(30, 41, 59, 0.8)',
        border: selected ? '2px solid #60a5fa' : '1px solid rgba(96, 165, 250, 0.2)',
        borderRadius: 2,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        transform: 'scale(1)',
        boxShadow: '0 8px 20px rgba(96, 165, 250, 0.2)',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          transform: 'scale(1.03)',
          boxShadow: '0 14px 32px rgba(96, 165, 250, 0.35)'
        }
      }}
    >
      {plan.popular && (
        <Chip
          label="MOST POPULAR"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#f59e0b',
            color: 'black',
            fontWeight: 700,
            fontSize: '0.65rem'
          }}
        />
      )}
      
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          {plan.name}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          {plan.originalPrice && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b', 
                textDecoration: 'line-through',
                fontSize: '0.9rem'
              }}
            >
              ${plan.originalPrice}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
            {hasDiscount && (
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b', textDecoration: 'line-through' }}
              >
                ${plan.price}
              </Typography>
            )}
            <Typography variant="h4" sx={{ color: hasDiscount ? '#22c55e' : '#60a5fa', fontWeight: 700 }}>
              ${finalPrice.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              /{plan.interval}
            </Typography>
          </Box>
          {plan.savings && (
            <Chip
              label={plan.savings}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                color: '#22c55e',
                fontWeight: 600
              }}
            />
          )}
          {charityPercent && (
            <Chip
              label={`💚 ${charityPercent}% to Charity`}
              size="small"
              sx={{
                mt: 1,
                ml: plan.savings ? 0.5 : 0,
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                color: '#4ade80',
                fontWeight: 500,
                fontSize: '0.65rem'
              }}
            />
          )}
        </Box>

        <Box sx={{ textAlign: 'left', mb: 3 }}>
          {plan.features.map((feature, idx) => (
            <Typography 
              key={idx} 
              variant="body2" 
              sx={{ 
                color: '#94a3b8', 
                mb: 0.5,
                fontSize: '0.8rem'
              }}
            >
              {feature}
            </Typography>
          ))}
        </Box>

        <Button
          fullWidth
          variant={selected ? 'contained' : 'outlined'}
          startIcon={<CreditCardIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onSubscribe();
          }}
          sx={{
            backgroundColor: selected ? '#3b82f6' : 'transparent',
            borderColor: '#3b82f6',
            color: selected ? 'white' : '#3b82f6',
            '&:hover': {
              backgroundColor: selected ? '#2563eb' : 'rgba(59, 130, 246, 0.1)'
            }
          }}
        >
          {plan.trialDays > 0 ? `Start Free Trial` : 'Subscribe'}
        </Button>
      </CardContent>
    </Card>
  );
}

export default SubscriptionPanel;
